// #region Dependencies
import { useEffect, useRef, useState, useCallback } from 'react'
import animationStyles from './animationStyles'
import styled from 'styled-components'
import { sharedTextStyle } from '../Text/Text'
// #endregion Dependencies

const StyledTextItem = styled.div.attrs((props) => ({
  as: props.tag,
  style: {
    display: 'inline-block',
    transform: `${props.extraStyle.translate.type}(${props.extraStyle.translate.value}${props.extraStyle.translate.unit})`,
    opacity: props.extraStyle.opacity
  }
}))`
  ${sharedTextStyle}
`

const TextSlider = ({
  tag = 'span',
  texts,
  effect = 'simple',
  animationDuration = 500,
  stopDuration = 3000,
  ...props
}) => {
  const selectedAnimation = effect
  const animationRef = useRef(null)
  const [extraStyle, setExtraStyle] = useState(
    animationStyles[selectedAnimation][0]
  )
  const textArrIndex = useRef(0)
  const previousTime = useRef(null)
  const currentRoundStartTime = useRef(0)
  const singleRoundDuration = stopDuration + 2 * animationDuration
  const easeOutQuad = (t) => t * (2 - t)
  const text = texts[textArrIndex.current]

  useEffect(() => {
    setExtraStyle(animationStyles[selectedAnimation][0])
  }, [selectedAnimation])

  const runShowTextAnimation = (elapsed) => {
    const showingTranlateInitial =
      animationStyles[selectedAnimation][1].translate.value
    const showingTranlateDiffrence =
      animationStyles[selectedAnimation][1].translate.value -
      animationStyles[selectedAnimation][0].translate.value
    const showingTiming = easeOutQuad(
      (elapsed - currentRoundStartTime.current) / animationDuration
    )
    setExtraStyle((s) => ({
      translate: {
        ...s.translate,
        value:
          showingTranlateInitial - showingTranlateDiffrence * showingTiming
      },
      opacity: showingTiming
    }))
  }

  const runHideTextAnimation = (elapsed) => {
    const hidingTraslateInitial =
      animationStyles[selectedAnimation][0].translate.value
    const hidingTranslateDiffrence =
      animationStyles[selectedAnimation][0].translate.value -
      animationStyles[selectedAnimation][1].translate.value
    const hidingOpacityInitial = animationStyles[selectedAnimation][0].opacity
    const hidingTiming = easeOutQuad(
      (elapsed -
        (currentRoundStartTime.current +
          singleRoundDuration -
          animationDuration)) /
        animationDuration
    )
    setExtraStyle((s) => ({
      translate: {
        ...s.translate,
        value: hidingTraslateInitial - hidingTranslateDiffrence * hidingTiming
      },
      opacity: hidingOpacityInitial - hidingTiming
    }))
  }

  const updateTextIndex = () => {
    if (textArrIndex.current === texts.length - 1) {
      textArrIndex.current = 0
    } else {
      textArrIndex.current = textArrIndex.current + 1
    }
  }
  const updateRoudStartTime = () => {
    currentRoundStartTime.current += singleRoundDuration
  }

  const runAnimation = useCallback(
    (timestamps) => {
      if (previousTime.current === null) previousTime.current = timestamps
      const elapsed = timestamps - previousTime.current

      if (
        elapsed > currentRoundStartTime.current &&
        elapsed < currentRoundStartTime.current + animationDuration
      ) {
        runShowTextAnimation(elapsed)
      }

      if (
        elapsed >
          currentRoundStartTime.current +
            singleRoundDuration -
            animationDuration &&
        elapsed < currentRoundStartTime.current + singleRoundDuration
      ) {
        runHideTextAnimation(elapsed)
      }

      if (elapsed > currentRoundStartTime.current + singleRoundDuration) {
        updateRoudStartTime()
        updateTextIndex()
      }

      animationRef.current = window.requestAnimationFrame(runAnimation)
    },
    [selectedAnimation, texts.length, singleRoundDuration, animationDuration]
  )

  useEffect(() => {
    animationRef.current = window.requestAnimationFrame(runAnimation)

    return () => window.cancelAnimationFrame(animationRef.current)
  }, [runAnimation, effect])

  return (
    <StyledTextItem extraStyle={extraStyle} tag={tag} {...props}>
      {text}
    </StyledTextItem>
  )
}

export default TextSlider
